/** @jsx jsx */
import { useContext, useRef } from "react"
import { Text, Button, Grid, Box, jsx } from "theme-ui"
import { useStaticQuery, graphql } from "gatsby"
import Slider from "react-slick"
import { animated as a } from "react-spring"
import Image from "gatsby-image"

import Bounce from "../../hooks/useBounce"
import { CaseBuilderModal } from "../CaseBuilderModal"
import { Indicator } from "./Icons"
import { Eye } from "../svg"

import { Context } from "../../context/MainContext"
import { ShopifyContext } from "../../context/ShopifyContext"
import { CarouselPrevious, CarouselNext } from "../svg"

import enums from "../../util/enums"

function MobileGrid({
  backgroundImage,
  bundleSize,
  colors,
  flavors,
  index,
  isOpen,
  numItems,
  selectedVariants,
  setIndex,
  setIsOpen,
  transitions,
}) {
  const slider = useRef(null)
  const { checkout, addBundleToCart } = useContext(ShopifyContext)
  const {
    store: { caseBuilderMode },
    setCaseBuilderMode,
    toggleFlavorNotesOpen,
  } = useContext(Context)

  const settings = {
    arrows: false,
    controls: false,
    centerMode: true,
    focusOnSelect: true,
    slidesToShow: 1,
    speed: 500,
    infinite: true,
    swipeToSlide: true,
    variableWidth: true,
    afterChange: idx => setIndex(idx),
  }

  const styles = {
    container: {
      display: ["block", "block", "none", "none"],
      width: "100%",
    },
    heading: {
      display: ["block", "block", "none", "none"],
      fontSize: "32px",
      width: "100%",
      textAlign: "center",
      marginBottom: "8px",
      color: flavors[index].primaryColor,
    },
    grid: {
      display: ["grid", "grid", "none", "none"],
      gridTemplateRows: "auto 1fr 75px",
      gridTemplateColumns: "1fr",
      position: "relative",
      height: "auto",
      width: "100%",
      gridGap: "0px",
    },
    flavorList: {
      transform: "translateX(-8px)",
      overflowX: "scroll",
      overflowY: "hidden",
      marginBottom: "15px",
      whiteSpace: "nowrap",
      div: {
        cursor: "pointer",
        display: "inline-block",
        padding: "0 0.25em",
      },
    },
    flavorText: {
      textAlign: "center",
      "@media screen and (max-width: 500px)": {
        fontSize: "32px",
      },
      "@media screen and (max-width: 400px)": {
        fontSize: "28px",
      },
      p: {
        display: "inline-block",
      },
      svg: {
        margin: "0px 0.25em 0.25em 0px",
        fontSize: "0.8em",
      },
    },
    image: {
      gridColumn: "1 / -1",
      gridRow: 2,
      zIndex: 0,
    },
    button: {
      borderRadius: "100px",
      fontSize: ["28px", "36px"],
      gridRow: 2,
      gridColumn: 1,
      width: "100%",
      zIndex: 0,
      color: flavors[index].primaryColor,
      backgroundColor: "#FFF",
      alignSelf: "end",
      transform: "translateY(50%)",
    },
    flavorNotes: {
      alignSelf: "start",
      height: "55px",
      justifySelf: "start",
      zIndex: 1,
      gridColumn: 1,
      gridRow: 2,
      padding: "0px",
      background: "transparent",
      fontSize: "14px",
      margin: "25px 20px",
    },
    slider: {
      width: "100vw",
      transform: "translateX(-8px)",
      margin: "16px 0px",
      ".slick-track": {
        display: "flex",
        alignItems: "center",
        width: "auto",
      },
      ".slick-slide": {
        whiteSpace: "nowrap",
        transition: "opacity 1000ms cubic",
        padding: ["0 20px", "0 20px", "0 45px"],
      },
    },
    controls: {
      margin: "0px",
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      button: {
        display: "flex",
        svg: {
          height: "20px",
        },
        margin: "0px",
        padding: "5px",
        background: "transparent",
      },
    },
  }

  const data = useStaticQuery(graphql`
    query MobileCaseHeader {
      sanityCaseBuilder(_id: { eq: "caseBuilder" }) {
        headlineLeft_view
      }
    }
  `)

  const { headlineLeft_view } = data.sanityCaseBuilder

  return (
    <Box sx={styles.container}>
      <CaseBuilderModal
        backgroundImage={backgroundImage}
        colors={colors}
        numItems={numItems}
        selectedVariants={selectedVariants}
        addBundleToCart={addBundleToCart}
        flavors={flavors}
        bundleSize={bundleSize}
      />
      <Text as="h2" variant="body" sx={styles.heading}>
        {headlineLeft_view || "Our Pints"}
      </Text>
      <Box sx={styles.controls}>
        <Bounce>
          <Button
            aria-label="Previous"
            onClick={() => slider.current.slickPrev()}
          >
            <CarouselPrevious color={flavors[index].primaryColor} />
          </Button>
        </Bounce>
        <Bounce>
          <Button aria-label="Next" onClick={() => slider.current.slickNext()}>
            <CarouselNext color={flavors[index].primaryColor} />
          </Button>
        </Bounce>
      </Box>
      <Slider ref={slider} {...settings} sx={styles.slider}>
        {flavors.map((flavor, idx) => (
          <Text
            key={flavor.title}
            sx={{
              ...styles.flavorText,
              color: flavor.primaryColor,
              // opacity: idx === index ? 0.5 : 1,
            }}
          >
            <Indicator
              style={{ transition: "opacity 250ms linear" }}
              opacity={idx === index ? 1 : 0}
              color={flavor.primaryColor}
            />
            <Text as="p">{flavor.title}</Text>
          </Text>
        ))}
      </Slider>
      <Grid sx={styles.grid}>
        <Button onClick={() => toggleFlavorNotesOpen()} sx={styles.flavorNotes}>
          <Eye
            primary={
              isOpen
                ? flavors[index].secondaryColor
                : flavors[index].primaryColor
            }
            secondary={
              isOpen
                ? flavors[index].primaryColor
                : flavors[index].secondaryColor
            }
          />
          <Text as="span" variant="secondary" sx={{ marginLeft: "10px" }}>
            {isOpen ? "CLOSE" : "MORE INFO"}
          </Text>
        </Button>

        {/* IMAGE  */}
        {transitions.map(({ item, props, key }) => (
          <a.div style={props} key={key} sx={styles.image}>
            <Image
              style={{
                width: "100%",
                height: "100%",
              }}
              imgStyle={{
                backgroundColor: item.backgroundColor,
                objectFit: "cover",
                objectPosition: "50% 50%",
              }}
              fluid={item.mobileImage.localFile.childImageSharp.fluid}
              alt={item.title}
            />
          </a.div>
        ))}
        <Button
          sx={styles.button}
          onClick={() => {
            if (caseBuilderMode === enums.mode.view) {
              setCaseBuilderMode(enums.mode.bundle)
              setIsOpen(false)
            }
            if (caseBuilderMode === enums.mode.bundle) {
              if (numItems !== bundleSize) return
              addBundleToCart(checkout.bundle)
            }
          }}
        >
          {"Build Your Case"}
        </Button>
      </Grid>
    </Box>
  )
}

export default MobileGrid
